import React, { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { PurchaseStatistic } from "interface/purchaseStatistic";
import { formatNumber } from "utils/formatters"

interface PurchaseStatisticMobileListProps {
	items: PurchaseStatistic[];
	loading: boolean;
}

interface PurchaseStatisticGroup {
	key: string;
	buyerId: number;
	buyerName: string;
	vesselRegistrationId: string;
	vesselName: string;
	items: PurchaseStatistic[];
	totalWeight: number;
	totalAmount: number;
}

const PurchaseStatisticMobileList: FC<PurchaseStatisticMobileListProps> = ({
	items,
	loading,
}) => {
	const { t } = useTranslation();

	const groupedItems = useMemo<PurchaseStatisticGroup[]>(() => {
		const groupMap = new Map<string, PurchaseStatisticGroup>();

		items.forEach((item) => {
			const buyerId = Number(item.buyerId || 0);
			const vesselRegistrationId = item.vesselRegistrationId || "";
			const vesselName = item.vesselName || "";
			const key = `${buyerId}-${vesselRegistrationId}-${vesselName}`;

			if (!groupMap.has(key)) {
				groupMap.set(key, {
					key,
					buyerId,
					buyerName: item.buyerName || "-",
					vesselRegistrationId,
					vesselName,
					items: [],
					totalWeight: 0,
					totalAmount: 0,
				});
			}

			const currentGroup = groupMap.get(key)!;
			currentGroup.items.push(item);
			currentGroup.totalWeight += Number(item.netLiveWeightKg || 0);
			currentGroup.totalAmount += Number(item.amount || 0);
		});

		return Array.from(groupMap.values());
	}, [items]);

	const empty = !loading && groupedItems.length === 0;

	return (
		<div className="purchase-statistic-list-mobile">
			{empty && (
				<div className="purchase-statistic-list-mobile-empty">{t("NoRows")}</div>
			)}

			{groupedItems.map((group) => (
				<div key={group.key} className="purchase-statistic-list-card">
					<div className="purchase-statistic-list-card-header">
						<div className="purchase-statistic-list-card-buyer">
							{group.buyerName || "-"}
						</div>
						<div className="purchase-statistic-list-card-buyer-id">
							{group.buyerId ? `${group.buyerId}` : "-"}
						</div>
						<div className="purchase-statistic-list-card-vessel">
							{group.vesselRegistrationId || "-"}
							{group.vesselName ? ` - ${group.vesselName}` : ""}
						</div>
					</div>

					<div className="purchase-statistic-list-card-body">
						{group.items.map((item, index) => (
							<div
								key={`${group.key}-${item.productId}-${index}`}
								className="purchase-statistic-list-row"
							>
								<div className="purchase-statistic-list-row-main">
									<div className="purchase-statistic-list-row-title">
										{item.productName || "-"}
									</div>
								</div>
								<div className="purchase-statistic-list-row-metrics">
									<div>
										<span>{t("Weight")}</span>
										<strong>{formatNumber(item.netLiveWeightKg)} kg</strong>
									</div>
									<div>
										<span>{t("Price")}</span>
										<strong>{formatNumber(item.averagePrice)} kr</strong>
									</div>
									<div>
										<span>{t("Amount")}</span>
										<strong>{formatNumber(item.amount)} kr</strong>
									</div>
								</div>
							</div>
						))}
					</div>

					<div className="purchase-statistic-list-card-footer">
						<div>
							<span>{t("Weight")}</span>
							<strong>{formatNumber(group.totalWeight)} kg</strong>
						</div>
						<div>
							<span>{t("Amount")}</span>
							<strong>{formatNumber(group.totalAmount)} kr</strong>
						</div>
					</div>
				</div>
			))}

			{loading && <div className="purchase-statistic-list-mobile-loading">{t("Loading")}</div>}
		</div>
	);
};

export default PurchaseStatisticMobileList;